import {
  Actor,
  Collider,
  Explosive,
  Falling,
  Hazard,
  Static,
  Transform,
  Velocity,
} from '@/components'
import type { SystemContext } from '@/core/ecs/Scheduler'
import { applyKnockback, explode } from '@/game/abilities/handlers'
import { ArenaResource } from '@/game/arenas/Arena'
import { Events } from '@/game/events'
import { EventsResource, MatchResource, RngResource } from '@/game/resources'

/**
 * Arena-owned danger: burner anomalies that fire on a timer, stationary
 * vortices that pull constantly, and explosive barrels waiting for a hard hit.
 *
 * Hazards belong to the level rather than to a player, which is what makes
 * them interesting: both fighters have to read the same clock.
 */
export function hazardSystem({ world, dt }: SystemContext): void {
  const events = world.resource(EventsResource)
  // Nothing fires while players are locked in the countdown. With staggered
  // phases the hazards were going off before anyone could move, and the phase
  // offsets meant one player ate a burst 0.9 s in and the other at 2.6 s —
  // free, asymmetric instability handed out before the round even started.
  const live = world.resource(MatchResource).phase === 'fight'
  const targets = world
    .query([Transform, Velocity, Collider], { none: [Static, Falling] })
    .entities()

  for (const e of world.query([Hazard, Transform]).entities()) {
    const hazard = world.read(e, Hazard)
    const tr = world.read(e, Transform)
    if (hazard.flash > 0) hazard.flash = Math.max(0, hazard.flash - dt * 2.6)
    if (!live) continue

    if (hazard.kind === 'vortex' || hazard.kind === 'tornado') {
      if (hazard.kind === 'tornado') driftTornado(world, e, dt)

      for (const target of targets) {
        const t = world.read(target, Transform)
        const vel = world.read(target, Velocity)
        const dx = tr.x - t.x
        const dz = tr.z - t.z
        const dist = Math.hypot(dx, dz)
        if (dist > hazard.radius || dist < 1e-3) continue

        const falloff = 1 - dist / hazard.radius
        const nx = dx / dist
        const nz = dz / dist
        vel.x += nx * hazard.pull * falloff * dt
        vel.z += nz * hazard.pull * falloff * dt

        // A tornado also throws you around itself rather than straight in,
        // which is what makes it read as a tornado and not as a magnet.
        if (hazard.swirl !== 0) {
          vel.x += -nz * hazard.swirl * falloff * dt
          vel.z += nx * hazard.swirl * falloff * dt
        }
      }
      continue
    }

    // Burner and electro: a quiet stretch, a loud telegraph, then the burst.
    const prev = hazard.timer
    hazard.timer += dt
    const fireAt = hazard.interval
    const warnAt = hazard.interval - hazard.telegraph

    if (prev < warnAt && hazard.timer >= warnAt) {
      events.emit(Events.Hazard, {
        entity: e,
        kind: 'burnerCharge',
        x: tr.x,
        z: tr.z,
        radius: hazard.radius,
      })
    }

    if (hazard.timer >= fireAt) {
      hazard.timer -= fireAt
      hazard.flash = 1
      events.emit(Events.Hazard, {
        entity: e,
        kind: 'burnerFire',
        x: tr.x,
        z: tr.z,
        radius: hazard.radius,
      })
      events.emit(Events.Shake, { strength: 0.45, x: tr.x, z: tr.z })

      for (const target of targets) {
        const t = world.read(target, Transform)
        const dx = t.x - tr.x
        const dz = t.z - tr.z
        const dist = Math.hypot(dx, dz)
        if (dist > hazard.radius) continue

        const falloff = 1 - dist / hazard.radius
        const dirX = dist < 1e-4 ? 0 : dx / dist
        const dirZ = dist < 1e-4 ? 1 : dz / dist
        const isActor = world.has(target, Actor)
        applyKnockback(
          world,
          target,
          dirX,
          dirZ,
          hazard.power * (0.5 + 0.5 * falloff),
          isActor ? hazard.instability : 0,
          'hazard',
          e,
          t.x,
          t.z,
        )
      }
    }
  }

  updateExplosives(world, dt)
}

const TORNADO_SPEED = 3.4
/** Radians per second of the fixed weave. */
const TORNADO_TURN = 0.9
/** How far ahead the warning ring is painted, in seconds of travel. */
const TORNADO_LOOKAHEAD = 0.75
const TORNADO_WARN_INTERVAL = 0.4

/**
 * Wanders a tornado around the platform on a fixed, learnable path.
 *
 * It used to draw from the simulation RNG every single step, which made its
 * course unlearnable by construction: you could not read where it was going,
 * only where it was, so being caught by one was never a mistake you could have
 * avoided — and Michotte's causality window says an outcome with no readable
 * cause is not attributed to the player at all. Now it weaves on a sine, which
 * is still not a straight line but *is* something a player can anticipate, and
 * it announces the ground it is about to cover. See docs/PHYSICS.md §6.5.
 */
function driftTornado(world: SystemContext['world'], e: number, dt: number): void {
  const hazard = world.read(e, Hazard)
  const tr = world.read(e, Transform)
  const arena = world.resource(ArenaResource)

  if (hazard.driftX === 0 && hazard.driftZ === 0) {
    // One draw, at birth, so two tornados in one arena do not travel in step.
    const a = world.resource(RngResource).angle()
    hazard.driftX = Math.sin(a) * TORNADO_SPEED
    hazard.driftZ = Math.cos(a) * TORNADO_SPEED
  }

  hazard.timer += dt

  // A steady weave: same turn rate every frame, reversing on a slow cycle.
  const turn = Math.sin(hazard.timer * 0.7) * TORNADO_TURN * dt
  const cos = Math.cos(turn)
  const sin = Math.sin(turn)
  const dx = hazard.driftX * cos - hazard.driftZ * sin
  const dz = hazard.driftX * sin + hazard.driftZ * cos
  hazard.driftX = dx
  hazard.driftZ = dz

  // Steer home when the edge is close.
  const edge = -arena.distance(tr.x, tr.z)
  if (edge < hazard.radius * 0.9) {
    const cx = arena.def.centerX - tr.x
    const cz = arena.def.centerZ - tr.z
    const len = Math.hypot(cx, cz) || 1
    hazard.driftX += (cx / len) * 9 * dt
    hazard.driftZ += (cz / len) * 9 * dt
  }

  const speed = Math.hypot(hazard.driftX, hazard.driftZ) || 1
  hazard.driftX = (hazard.driftX / speed) * TORNADO_SPEED
  hazard.driftZ = (hazard.driftZ / speed) * TORNADO_SPEED

  tr.x += hazard.driftX * dt
  tr.z += hazard.driftZ * dt

  // The warning: a ring on the ground the tornado is about to walk over.
  if (hazard.timer - hazard.lastWarn >= TORNADO_WARN_INTERVAL) {
    hazard.lastWarn = hazard.timer
    world.resource(EventsResource).emit(Events.Hazard, {
      entity: e,
      kind: 'burnerCharge',
      x: tr.x + hazard.driftX * TORNADO_LOOKAHEAD,
      z: tr.z + hazard.driftZ * TORNADO_LOOKAHEAD,
      radius: hazard.radius * 0.75,
    })
  }
}

function updateExplosives(world: SystemContext['world'], dt: number): void {
  for (const e of world.query([Explosive, Transform]).entities()) {
    const explosive = world.read(e, Explosive)
    if (explosive.armed) continue

    explosive.fuse -= dt
    if (explosive.fuse > 0) continue

    const tr = world.read(e, Transform)
    explode(
      world,
      tr.x,
      tr.z,
      explosive.radius,
      explosive.power,
      explosive.instability,
      'barrel',
      e,
    )
    world.destroyEntity(e)
  }
}
